import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useState } from "react";
import Text from "./shared/Text";

/**
 * FeatureCompareAccordionsSection — side-by-side feature accordion cards
 * that let users expand any feature category and scan which features each
 * platform ships.
 *
 * Each card has a logo/wordmark header and a vertical stack of expandable
 * category rows. A category body lists feature rows as `status icon + label`.
 * Status is one of `yes` (green check), `no` (red cross), or `partial`
 * (orange warning triangle).
 */

export type FeatureCompareStatus = "yes" | "no" | "partial";

export type FeatureCompareRow = {
  status: FeatureCompareStatus;
  label: string;
};

export type FeatureCompareCategory = {
  name: string;
  rows: FeatureCompareRow[];
};

export type FeatureCompareSide = {
  label: string;
  logoSrc?: string;
  /** When true, renders the label text alongside the logoSrc image */
  showLabel?: boolean;
  categories: FeatureCompareCategory[];
};

export type FeatureCompareAccordionsData = {
  titleMuted: string;
  titleBright: string;
  subtitle?: string;
  primaryCta: CtaType;
  ctas?: CtaType[];
  competitor: FeatureCompareSide;
  graphy: FeatureCompareSide;
};

const statusIcon: Record<FeatureCompareStatus, { icon: typeof faCircleCheck; className: string }> = {
  yes: { icon: faCircleCheck, className: "text-[#2CA25F]" },
  no: { icon: faCircleXmark, className: "text-[#E5483C]" },
  partial: { icon: faTriangleExclamation, className: "text-[#E07C1A]" },
};

const CompareCard = ({ side, cardId }: { side: FeatureCompareSide; cardId: string }) => {
  const [openIdx, setOpenIdx] = useState<number>(0); // Top Features open by default

  return (
    <div className="flex flex-col overflow-hidden rounded-3xl border border-neutralBlack/10 bg-white">
      <div className="flex items-center justify-center px-6 py-6 md:py-8">
        {side.label.toUpperCase() === "GRAPHY" ? (
          <img
            src="/images/graphy_logo_black.svg"
            alt="Graphy"
            className="h-6 md:h-7"
          />
        ) : side.logoSrc ? (
          <div className={side.showLabel ? "flex items-center gap-2" : ""}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={side.logoSrc} alt={side.label} className="h-6 object-contain md:h-7" />
            {side.showLabel && (
              <Text
                as="div"
                variant="title-lg"
                className="text-[22px] font-normal tracking-wide text-neutralBlack"
              >
                {side.label}
              </Text>
            )}
          </div>
        ) : (
          <Text
            as="div"
            variant="title-lg"
            className="text-[22px] font-normal uppercase tracking-wide text-neutralBlack"
          >
            {side.label}
          </Text>
        )}
      </div>
      <div className="flex flex-col">
        {side.categories.map((cat, idx) => {
          const isOpen = openIdx === idx;
          const isLast = idx === side.categories.length - 1;
          return (
            <div key={`${cardId}-${cat.name}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left md:px-6"
              >
                <Text
                  as="span"
                  variant="title-sm"
                  className="text-[15px] !font-medium text-neutralBlack md:text-[16px]"
                >
                  {cat.name}
                </Text>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={classNames(
                    "h-4 w-4 shrink-0 text-neutralBlack/60 transition-transform duration-200",
                    isOpen ? "rotate-180" : "rotate-0"
                  )}
                  aria-hidden="true"
                />
              </button>
              {isOpen ? (
                <div className="px-5 pb-5 md:px-6">
                  <ul className="flex flex-col gap-3">
                    {cat.rows.map((row) => {
                      const spec = statusIcon[row.status];
                      return (
                        <li
                          key={row.label}
                          className="flex items-start gap-3"
                        >
                          <FontAwesomeIcon
                            icon={spec.icon}
                            className={`mt-0.5 h-4 w-4 shrink-0 ${spec.className}`}
                            aria-hidden="true"
                          />
                          <Text
                            as="span"
                            variant="body-md"
                            className="text-[14px] leading-relaxed text-darkSlate04"
                          >
                            {row.label}
                          </Text>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FeatureCompareAccordionsSection = ({
  titleMuted,
  titleBright,
  subtitle,
  primaryCta,
  ctas,
  competitor,
  graphy,
}: FeatureCompareAccordionsData) => {
  return (
    <section className="w-full bg-white">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <Text
          as="h2"
          variant="display-lg"
          className="mx-auto max-w-4xl text-center text-[28px] sm:text-[32px] md:text-[40px] lg:text-[48px]"
        >
          <span className="text-neutralBlack">{titleMuted}</span>
          <br />
          <span className="text-neutralBlack">{titleBright}</span>
        </Text>
        {subtitle ? (
          <Text
            as="p"
            variant="body-lg"
            className="mx-auto mt-5 max-w-3xl text-center text-darkSlate04"
          >
            {subtitle}
          </Text>
        ) : null}
        <div className="mt-8 flex items-center justify-center md:mt-10">
          <CtaButtonGroup
            ctas={ctas ?? [primaryCta]}
            gap="flex gap-3"
            fullWidthOnMobile={false}
          />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-2 md:gap-8">
          <CompareCard side={competitor} cardId="competitor" />
          <CompareCard side={graphy} cardId="graphy" />
        </div>
      </div>
      </div>
    </section>
  );
};

export default FeatureCompareAccordionsSection;
